import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Brand } from "@/components/Common";
import { LanguageSelector } from "@/components/LanguageSelector";
import { EMPTY_PROFILE, Field, ProfileFieldsForm } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [w, setW] = useState(null);
  const [v, setV] = useState(EMPTY_PROFILE);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/waiver").then((r) => setW(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!v.gender) return setError("Pilih jenis kelamin.");
    if (pin !== pin2) return setError("Konfirmasi PIN tidak sama.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        ...v, phone, email, pin, accept_waiver: agree, waiver_version: w?.waiver?.version,
      });
      signIn(data);
      nav("/app", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-8">
      <div className="mb-8 flex items-center justify-between">
        <Brand sub="daftar climber" className="h-20" />
        <div className="flex items-center gap-3">
          <LanguageSelector />
          <Link to="/" className="flex items-center gap-1 text-sm font-semibold text-[#1A3323]" data-testid="back-to-login-link">
            <ArrowLeft className="h-4 w-4" /> Masuk
          </Link>
        </div>
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1A3323] sm:text-4xl">Daftar sekali, manjat berkali-kali.</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Peserta di bawah {w?.minor_age_threshold ?? 17} tahun didaftarkan oleh wali: wali mendaftar dulu, lalu menambahkan profil anak di menu Profil.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-6 rounded-3xl bg-white/70 p-5 ring-1 ring-[#1A3323]/10 sm:p-7" data-testid="register-form">
        <ProfileFieldsForm v={v} set={setV} prefix="register" />
        <div className="grid gap-4 border-t pt-6 sm:grid-cols-2">
          <Field label="Nomor WhatsApp" hint="Nomor luar negeri? Awali dengan kode negara, mis. +60123456789">
            <Input className="h-11" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxxxxxxxx" required data-testid="register-phone-input" />
          </Field>
          <Field label="Email">
            <Input className="h-11" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com" required data-testid="register-email-input" />
          </Field>
          <Field label="PIN (6 digit)">
            <Input className="h-11 tracking-widest" type="password" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value)} required data-testid="register-pin-input" />
          </Field>
          <Field label="Ulangi PIN">
            <Input className="h-11 tracking-widest" type="password" inputMode="numeric" maxLength={6} value={pin2} onChange={(e) => setPin2(e.target.value)} required data-testid="register-pin-confirm-input" />
          </Field>
        </div>
        {w?.waiver && (
          <div className="space-y-3 border-t pt-6">
            <div className="font-bold text-[#1A3323]">{w.waiver.title} (v{w.waiver.version})</div>
            <div className="max-h-48 overflow-y-auto whitespace-pre-line rounded-xl bg-[#F5F3EB] p-4 text-sm leading-relaxed" data-testid="register-waiver-text">
              {w.waiver.content}
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={agree} onCheckedChange={(x) => setAgree(!!x)} data-testid="register-waiver-checkbox" />
              Saya telah membaca dan menyetujui waiver keselamatan.
            </label>
          </div>
        )}
        {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy || !agree} className="h-12 w-full rounded-full text-base font-bold" data-testid="register-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Daftar & Masuk
        </Button>
      </form>
    </div>
  );
}
